import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/student/PageHeader";
import { ModuleProgressActionsCard } from "@/components/student/ModuleProgressActionsCard";
import { InfiniteScroll } from "@/components/InfiniteScroll";
import { StudentExerciseAdapter } from "@/pages/Student/Exercises/adapter/StudentExerciseAdapter";
import { Colors, useLayoutCommon } from "@/contexts/LayoutContext";
import { fetchExercisesByModule } from "@/services/student/exercise.service";
import { getModuleInfo } from "@/services/student/module.service";
import { ModuleInfo } from "@/services/student/module.interfaces";

import { ExerciseItem } from "@/services/student/exercise.interfaces";
import { apiGetStudentExerciseAnswers } from "@/services/student/student-answers.api";
import {
  apiGetModuleProgress,
  apiResetModuleProgress,
  moduleProgressQueryKey,
  type StudentModuleProgress,
} from "@/services/student/module-progress.api";
import { moduleCompletionQueryKey } from "@/services/student/module-completion.api";
import { isPoolBasedProgression } from "@/domain/moduleProgressionMode";
import { useModuleCompletion } from "@/hooks/student/useModuleCompletion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAppToast } from "@/hooks/useAppToast";

const fetchItems = async (moduleId: string): Promise<ExerciseItem[]> => {
  try {
    const exercises = await fetchExercisesByModule(moduleId);
    const answers = await apiGetStudentExerciseAnswers(moduleId);

    return exercises.map((exercise) => {
      const answer = answers.find(
        (answer) => answer.exerciseId === exercise.id
      );

      const exerciseAnswer = answer && {
        isCorrect: answer?.studentAnswerIsCorrect,
        data: JSON.parse(answer.studentAnswerData),
      };

      return { ...exercise, answer: exerciseAnswer };
    });
  } catch (error) {
    console.error("Error fetching exercises:", error);
    return [];
  }
};

const itemKey = (item: ExerciseItem) => item.id;

const isModuleCompleted = (progress: StudentModuleProgress | null): boolean =>
  Boolean(
    progress &&
      progress.totalExercises > 0 &&
      progress.answeredExercises >= progress.totalExercises
  );

export const StudentExercisesPage = () => {
  const { setBackgroundColor } = useLayoutCommon();
  const { moduleId } = useParams<{ moduleId: string }>();
  const queryClient = useQueryClient();
  const toast = useAppToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [moduleInfo, setModuleInfo] = useState<ModuleInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exercisesRefreshKey, setExercisesRefreshKey] = useState(0);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const completion = useModuleCompletion(moduleId ?? null);
  const showModuleTestEntrypoint =
    Boolean(moduleId) &&
    !completion.isLoading &&
    completion.completionStatus != null &&
    isPoolBasedProgression(completion.completionStatus.progressionMode) &&
    completion.isCompleted &&
    !completion.isNextModuleAvailable;

  const { data: moduleProgress, isPending: progressPending } = useQuery({
    queryKey: moduleId
      ? moduleProgressQueryKey(moduleId)
      : ["module-progress", "__none__"],
    queryFn: () => apiGetModuleProgress(moduleId!),
    enabled: Boolean(moduleId),
  });

  const resetModuleMutation = useMutation({
    mutationFn: () => apiResetModuleProgress(moduleId!),
    onSuccess: async () => {
      toast.success("Progresso do módulo reiniciado.");
      setResetDialogOpen(false);
      if (moduleId) {
        await queryClient.invalidateQueries({
          queryKey: moduleProgressQueryKey(moduleId),
        });
        await queryClient.invalidateQueries({
          queryKey: moduleCompletionQueryKey(moduleId),
        });
      }
      setExercisesRefreshKey((k) => k + 1);
    },
    onError: () => {
      toast.error("Não foi possível reiniciar o módulo. Tente novamente.");
    },
  });

  const renderItem = (item: ExerciseItem, key: React.Key) => {
    if (!moduleId) {
      return <div key={key}>Erro: módulo não especificado</div>;
    }

    return (
      <div key={key}>
        <StudentExerciseAdapter item={item} moduleId={moduleId} />
      </div>
    );
  };

  useEffect(() => {
    if (moduleId) {
      setLoading(true);
      setError(null);
      getModuleInfo(moduleId)
        .then((moduleData) => {
          setModuleInfo(moduleData);
        })
        .catch((error) => {
          console.error("Error fetching module information:", error);
          setError(
            "Erro ao carregar informações do módulo. Por favor, tente novamente."
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setError(
        "Nenhum módulo foi especificado. Por favor, selecione um módulo para visualizar os exercícios."
      );
    }
  }, [moduleId]);

  useEffect(() => {
    setExercisesRefreshKey(0);
  }, [moduleId]);

  useEffect(() => {
    const state = location.state as { retryContentExercises?: boolean } | null;
    if (state?.retryContentExercises) {
      setExercisesRefreshKey((k) => k + 1);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  React.useEffect(() => {
    setBackgroundColor(Colors.WHITE);
  }, []);

  const fetchModuleItems = async (page: number): Promise<ExerciseItem[]> => {
    if (!moduleId) {
      throw new Error(
        "Módulo não especificado. Selecione um módulo para visualizar os exercícios."
      );
    }

    if (page === 1) {
      const items = await fetchItems(moduleId);
      console.log(
        `Carregando ${items.length} exercícios do módulo ${moduleId}`
      );
      return items;
    }

    return [];
  };

  if (error) {
    return (
      <div className="max-w-[1800px] w-full">
        <div className="w-full bg-red-50 rounded-xl p-6 mt-6">
          <div className="text-red-600 text-center font-medium text-lg">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1800px] w-full">
      <PageHeader
        showAvatar={true}
        showActions={true}
        title={
          <div className="flex flex-col">
            <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.07em] text-gray-400 mb-1">
              {moduleInfo?.name || "Carregando..."}
            </span>
            <h1 className="text-[1.75rem] font-bold leading-[1.15] text-gray-900">
              {moduleInfo?.title}
            </h1>
            <p className="text-[0.9375rem] text-gray-500 mt-1 leading-relaxed whitespace-pre-line">
              {moduleInfo?.summarizedDescription}
            </p>
          </div>
        }
      />

      <div className="mb-6 w-full max-w-[1227px] mx-auto">
        <ModuleProgressActionsCard
          className="m-2 sm:m-4"
          progressPending={Boolean(moduleId) && progressPending}
          moduleProgress={moduleProgress}
          onReiniciarClick={() => setResetDialogOpen(true)}
          reiniciarDisabled={!moduleId || loading}
          showModuleTestEntrypoint={showModuleTestEntrypoint}
          moduleTestEnabled={
            Boolean(moduleId) && isModuleCompleted(moduleProgress ?? null)
          }
          onModuleTestClick={() => {
            if (moduleId) {
              void navigate(`/student/modules/${moduleId}/test`);
            }
          }}
        />
      </div>

      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reiniciar este módulo?</DialogTitle>
            <DialogDescription>
              Todas as respostas e o progresso dos exercícios deste módulo serão
              apagados. Testes de nível, testes de módulo e desbloqueios não são
              alterados.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setResetDialogOpen(false)}
              disabled={resetModuleMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => resetModuleMutation.mutate()}
              disabled={resetModuleMutation.isPending}
            >
              {resetModuleMutation.isPending ? "Reiniciando…" : "Reiniciar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="w-full bg-gray-50 rounded-xl p-6 mt-6">
        <div className="w-full max-w-[1227px] mx-auto">
          <InfiniteScroll<ExerciseItem>
            key={`${moduleId}-${exercisesRefreshKey}`}
            showRefreshButton={false}
            fetchItems={fetchModuleItems}
            renderItem={renderItem}
            itemKey={itemKey}
          />
        </div>
      </div>
    </div>
  );
};
